<template>
  <div>
    <!-- BUTTON -->
    <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->
    <Component
      :is="hasAlternates||isProductPage ? 'button': 'div'"
      class="flex items-center gap-3 text-grey-300"
      :class="{ 'transition-colors ease-in-out hover:text-primary-900': hasAlternates }"
      :aria-expanded="hasAlternates || isProductPage ? show : null"
      :aria-controls="hasAlternates || isProductPage ? 'lang-switcher-dropdown': null"
      @click="show ? leave() : hasAlternates || isProductPage ? enter() : null"
      @blur="leave()"
    >
      <!-- Globe Icon -->
      <IconLanguage class="size-6" />
      <!-- Current locale -->
      <span class="mt-1 leading-none typo-subtitle">
        {{ locales.find(locale => locale.code === currentLocale)?.name }}
      </span>
    </Component>

    <!-- Dropdown Background -->
    <NavDropdownBackground :sub-nav-width="subNavWidth" :sub-nav-height="subNavHeight" />

    <!-- DROPDOWN -->
    <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->
    <Container
      v-show="show"
      id="lang-switcher-dropdown"
      use-full-width
      class="absolute inset-x-0 top-full py-12"
    >
      <AnimatedContainer ref="animContainer" :delay="0.3">
        <div class="fade-in">
          <!-- Title -->
          <div class="font-semibold typo-subtitle">
            {{ langSwitcher?.title_lang_switcher ? langSwitcher.title_lang_switcher : 'Dieser Titel kann in den Globalen Inhalten definiert werden.' }}
          </div>

          <ul class="mt-9 flex flex-wrap gap-x-[12%]">
            <li v-for="[key, localesGroup] in Object.entries(localesByGroup)" :key="key">
              <!-- Group Title -->
              <div class="mb-3 font-semibold typo-subtitle">
                {{ $t(`lang_switcher.${key}`) }}
              </div>

              <!-- Languages -->
              <ul class="space-y-3">
                <!-- TODO: locale.code -> localce.path ? -->
                <li v-for="locale in localesGroup" :key="locale.code">
                  <button
                    type="button"
                    :aria-label="$t('switch_language')"
                    class="transition-colors ease-in-out hover:text-primary-900"
                    :class="{ 'text-primary-900': locale.code === currentLocale }"
                    @click="switchLanguage(locale.code)"
                    @focus="enter()"
                    @blur="leave()"
                    @keydown.escape.prevent="leave()"
                  >
                    {{ locale.display }}
                  </button>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </AnimatedContainer>
    </Container>
  </div>
</template>

<script setup lang="ts">
type Alternate = {
  path: string
  locale: string
  url?: string
  permalink?: string
}

const switchLocalePath = useSwitchLocalePath()

const { locale: currentLocale, locales } = useI18n()
const route = useRoute()
const entry = inject(EntryInjectionKey, null)
const globals = inject(GlobalsKey, null)

const show = ref(false)
let leaveTimeout: any = null
const subNavWidth = ref(0)
const subNavHeight = ref(0)
const animContainer = ref<any>(null)

const langSwitcher = computed(() =>
  globals?.value.lang_switcher as {
    title_lang_switcher?: string | null
    handle: string
  } | null)

const isProductPage = computed(() => {
  return route.name?.startsWith('products-all')
})

const alternates = computed<Alternate[]>(() => {
  return Array.isArray(entry?.value?.alternates) ? entry.value.alternates.filter(e => !!e) as Alternate[] : []
})

const hasAlternates = computed(() => {
  return alternates.value?.length > 1
})

const localesByGroup = computed(() => {
  let filteredLocales: any[] = []

  if (isProductPage.value) {
    // If it's a product page, show all locales
    filteredLocales = locales.value
  } else {
    // For all other pages filter locales that match the alternate codes
    if (!hasAlternates.value) {
      return []
    }
    // Extract locale codes from alternates
    const alternatePaths = alternates.value?.map(alt => alt.path)

    filteredLocales = locales.value.filter(locale => alternatePaths?.includes(locale.code))
  }

  // Group the filtered locales by their 'group'
  return filteredLocales?.reduce((acc, locale) => {
    if (!acc[locale.group]) {
      acc[locale.group] = []
    }

    acc[locale.group].push(locale)

    return acc
  }, {} as Record<string, any[]>)
})

async function enter() {
  if (leaveTimeout !== null) {
    clearTimeout(leaveTimeout)
    leaveTimeout = null
  }

  // Do nothing if the dropdown is already open
  if (show.value) {
    return
  }

  setShow(true)
  animContainer.value?.RestartAnimation()

  // Wait one tick until the lang switcher dropdown is rendered
  await nextTick()

  // Get the height of the lang switcher dropdown to set the height of the background
  const subNav = document.getElementById('lang-switcher-dropdown')
  subNavWidth.value = subNav?.offsetWidth || 0
  subNavHeight.value = subNav?.offsetHeight || 0
}

function leave() {
  if (leaveTimeout === null) {
    leaveTimeout = setTimeout(() => {
      animContainer.value?.ReverseAnimation()
      subNavWidth.value = 0
      subNavHeight.value = 0
      // Wait for the animation before hiding the dropdown
      setTimeout(() => {
        setShow(false)
      }, 100)
    }, 100)
  }
}

function switchLanguage(selectedPath: string) {
  leave()

  if (!isProductPage.value && Array.isArray(alternates.value) && alternates.value.length) {
    const url = alternates.value.find(a => a.path === selectedPath)?.permalink

    if (url && url !== route.path) {
      switchLocalePath(selectedPath) // this is needed to work with different domains for different languages
      return navigateTo(url, {
        external: true,
      })
    }
  }

  navigateTo(switchLocalePath(selectedPath), {
    external: true, // this is needed to work with different domains for different languages
  })
}

function setShow(value: boolean) {
  show.value = value
  if (value) {
    const mainNavLevel1 = document.getElementById('main-nav')
    // Add 'pointer-events-none' class to the element
    mainNavLevel1?.classList.add('pointer-events-none')
  } else {
    const mainNavLevel1 = document.getElementById('main-nav')
    // Remove class 'pointer-events-none' from the element
    mainNavLevel1?.classList.remove('pointer-events-none')
  }
}

onBeforeMount(() => {
  const mainNavLevel1 = document.getElementById('main-nav')
  // Make sure the class 'pointer-events-none' is removed
  mainNavLevel1?.classList.remove('pointer-events-none')
})
</script>
